import { ServerSideSort, SortState } from '@/components/tables/AxleTable';
import { useVisitFilterDialogFilters } from '@/components/visits/visitFilterState';
import { AllVisitsContext } from '@/pages/AllVisitsPage/AllVisits.context';
import { AllVisitsPageNS } from '@/pages/AllVisitsPage/AllVisitsPage.types';
import { VIEW_FILTERS } from '@/pages/AllVisitsPage/viewFilters';
import { createEffectiveDateInRangeFilter } from '@/types/visit';
import { Ordering, VisitFilter, VisitOrdering } from 'gql-types';
import { FC, ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Maps a table column field name to a VisitOrdering input.
 * Returns undefined if the field is not server-side sortable.
 */
function sortStateToVisitOrdering(sort: SortState | null): VisitOrdering | undefined {
    if (!sort) {
        return undefined;
    }
    const direction: Ordering = sort.direction === 'asc' ? Ordering.ASC : Ordering.DESC;
    switch (sort.sortedByField) {
        case 'sortDate':
            return { tentativeDate: direction };
        case 'patient.fullName':
            return { patient: { fullName: direction } };
        case 'fulfillmentWindow.startDate':
            return { fulfillmentWindowStartDate: direction };
        default:
            return undefined;
    }
}

interface IBulkTableContext {
    visitFilters: VisitFilter;
    visitOrdering: VisitOrdering | undefined;
    serverSideSort: ServerSideSort;
}

const BulkTableContext = createContext<IBulkTableContext | null>(null);

type BulkTableContextProviderProps = {
    children: ReactNode;
    view: AllVisitsPageNS.PrimaryTab;
};

type UseBulkViewFiltersResult = {
    visitFilters: VisitFilter;
};

type UseBulkViewFiltersOptions = {
    view?: AllVisitsPageNS.PrimaryTab;
};

type UseBulkViewFilters = (options: UseBulkViewFiltersOptions) => UseBulkViewFiltersResult;

export const useBulkViewFilters: UseBulkViewFilters = (options) => {
    const { view } = options;
    const {
        dateRange: [[start, end]],
        search: [, , searchTerm],
    } = useContext(AllVisitsContext);

    const visitDialogFilters = useVisitFilterDialogFilters();

    const visitFilters = useMemo(
        (): VisitFilter => ({
            ...visitDialogFilters,
            ...(view ? VIEW_FILTERS[view] : {}),
            // The dialog's explicit outcome filter (if any) overrides the view's
            // default `outcome: NONE` rule on the Needs tabs. Without this, a user
            // who picks "Completed" in the dialog still sees only outcome=NONE
            // visits on Needs Services / Clinician / Tentative Date.
            ...(visitDialogFilters.outcome ? { outcome: visitDialogFilters.outcome } : {}),
            patient: { ...visitDialogFilters.patient, searchTerm },
            AND: createEffectiveDateInRangeFilter(start, end),
        }),
        [view, visitDialogFilters, start, end, searchTerm],
    );

    return {
        visitFilters,
    };
};

export const BulkTableContextProvider: FC<BulkTableContextProviderProps> = (props) => {
    const { children, view } = props;
    const { visitFilters } = useBulkViewFilters({ view });
    // Default to ascending sort by date so the indicator on the Date column
    // matches the server-side ordering produced by sortStateToVisitOrdering.
    const [sort, setSort] = useState<SortState | null>({
        sortedByField: 'sortDate',
        direction: 'asc',
    });

    const visitOrdering = useMemo(() => sortStateToVisitOrdering(sort), [sort]);

    const onSort = useCallback((state: SortState | null) => setSort(state), []);
    const serverSideSort = useMemo((): ServerSideSort => [sort, onSort], [sort, onSort]);

    const value = useMemo(
        () => ({
            visitFilters,
            visitOrdering,
            serverSideSort,
        }),
        [visitFilters, visitOrdering, serverSideSort],
    );

    return <BulkTableContext.Provider value={value}>{children}</BulkTableContext.Provider>;
};

export const useBulkTableContext = () => {
    const context = useContext(BulkTableContext);
    if (!context) {
        throw new Error('useBulkTableContext must be used within a BulkTableContextProvider');
    }
    return context;
};
